import type { FC, ReactNode } from 'react'
import type { TileCell } from '@/components/landing/tiles'

import Image from 'next/image'
import { useState } from 'react'
import {
    CircleCheck,
    Gauge,
    ScrollText,
    ShieldCheck,
    Undo2
} from 'lucide-react'
import { cn } from '@/lib/utils'
import { Reveal } from '@/components/generals/reveal'
import { LogoMark } from '@/components/generals/logo'
import { SectionBadge } from '@/components/landing/section-badge'
import { GlassTiles } from '@/components/landing/tiles'
import { art } from '@/constants/art'

const POINTS = [
    {
        icon: ShieldCheck,
        title: 'Approval gates',
        body: 'Require a sign-off before an agent moves money, emails a customer or changes a record.'
    },
    {
        icon: Gauge,
        title: 'Confidence thresholds',
        body: 'Decisions below the confidence you set go to the right teammate, with the full context attached.'
    },
    {
        icon: ScrollText,
        title: 'A complete audit trail',
        body: 'Every action, input and decision is logged, searchable and ready for review.'
    }
]

const REASONS = [
    'The carrier confirms the order arrived 3 days late',
    'Maya asked for a refund, not a replacement',
    'The amount is above your $200 auto-approve limit'
]

const PANEL_TILES: TileCell[] = [[0, 0], [1, 1], [0, 2]]
const PANEL_TILES_ALT: TileCell[] = [[1, 0], [0, 1], [2, 1], [1, 2]]

type Decision = 'waiting' | 'approved'

const ApprovalDemo: FC = (): ReactNode => {
    const [decision, setDecision] = useState<Decision>('waiting')
    const [showReasons, setShowReasons] = useState(false)

    const approved = decision === 'approved'

    return (
        <div className='w-full max-w-[390px] rounded-[26px] border border-white/80 bg-white/90 p-5 text-left shadow-[0_30px_70px_-34px_rgba(24,32,78,0.55)] backdrop-blur-xl sm:p-6'>
            <p className='sr-only'>
                Interactive example of an approval request
            </p>

            <div className='flex items-center gap-3'>
                <span className='flex size-10 shrink-0 items-center justify-center rounded-full bg-meadow text-ink'>
                    <LogoMark className='h-3' />
                </span>

                <div className='min-w-0 flex-1'>
                    <p className='text-[15px] font-semibold text-ink'>
                        Refund Agent
                    </p>

                    <p className='text-[12.5px] text-muted-foreground'>
                        Asked 2 minutes ago
                    </p>
                </div>

                <span
                    className={cn(
                        'inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11.5px] font-semibold',
                        approved ? 'bg-sage text-ink' : 'bg-[#fdebd9] text-[#7c3a12]'
                    )}
                >
                    <span className={cn('size-1.5 rounded-full', approved ? 'bg-moss' : 'bg-[#d9762f]')} />
                    {approved ? 'Approved' : 'Needs you'}
                </span>
            </div>

            <p className='mt-5 text-[16px] leading-relaxed text-ink'>
                Issue a <strong className='font-semibold'>$240.00</strong> refund to Maya Chen for order #4821?
            </p>

            <p className='mt-3 flex items-center gap-2 rounded-2xl bg-mist px-3.5 py-2.5 text-[13px] text-ink/80'>
                <Gauge aria-hidden className='size-4 shrink-0 text-muted-foreground' />
                Confidence 72%, below your 85% threshold
            </p>

            {showReasons && (
                <ul id='approval-reasons' className='mt-3 space-y-2 px-1 text-[13px] leading-snug text-ink/80'>
                    {REASONS.map((reason) => (
                        <li key={reason} className='flex gap-2'>
                            <span aria-hidden className='mt-[7px] size-1 shrink-0 rounded-full bg-ink/50' />
                            {reason}
                        </li>
                    ))}
                </ul>
            )}

            <div aria-live='polite'>
                {approved ? (
                    <div className='mt-5 flex items-center justify-between gap-3 rounded-2xl bg-sage px-4 py-3'>
                        <span className='flex items-center gap-2 text-[13.5px] font-medium text-ink'>
                            <CircleCheck aria-hidden className='size-4 text-moss' />
                            Approved. Refund sent to Maya.
                        </span>

                        <button
                            type='button'
                            onClick={() => setDecision('waiting')}
                            className='inline-flex cursor-pointer items-center gap-1 rounded-full px-2 py-1 text-[12.5px] font-medium text-ink/75 transition-colors hover:bg-white/60 hover:text-ink focus-visible:ring-2 focus-visible:ring-moss focus-visible:outline-none'
                        >
                            <Undo2 aria-hidden className='size-3.5' />
                            Undo
                        </button>
                    </div>
                ) : (
                    <div className='mt-5 grid grid-cols-2 gap-2'>
                        <button
                            type='button'
                            onClick={() => setDecision('approved')}
                            className='h-11 cursor-pointer rounded-full bg-moss text-[14.5px] font-semibold text-white transition-colors hover:bg-moss-deep focus-visible:ring-2 focus-visible:ring-moss focus-visible:ring-offset-2 focus-visible:outline-none'
                        >
                            Approve
                        </button>

                        <button
                            type='button'
                            aria-expanded={showReasons}
                            aria-controls='approval-reasons'
                            onClick={() => setShowReasons((open) => !open)}
                            className='h-11 cursor-pointer rounded-full border border-border bg-white text-[14.5px] font-semibold text-ink transition-colors hover:bg-mist focus-visible:ring-2 focus-visible:ring-moss focus-visible:ring-offset-2 focus-visible:outline-none'
                        >
                            {showReasons ? 'Hide reasoning' : 'See reasoning'}
                        </button>
                    </div>
                )}
            </div>
        </div>
    )
}

export const OversightSection: FC = (): ReactNode => {
    const { meadow } = art

    return (
        <section
            id='oversight'
            aria-labelledby='oversight-title'
            className='relative isolate overflow-hidden bg-white py-24 sm:py-32'
        >
            <div className='mx-auto grid w-full max-w-[1180px] items-center gap-14 px-5 sm:px-8 lg:grid-cols-[1fr_1.08fr] lg:gap-20'>
                <Reveal>
                    <SectionBadge>Human in the loop</SectionBadge>

                    <h2
                        id='oversight-title'
                        className='mt-6 font-heading text-[clamp(2.1rem,3.1vw,2.8rem)] leading-[1.18] tracking-[-0.03em] text-balance text-ink'
                    >
                        Autonomy, with a human hand on the wheel.
                    </h2>

                    <p className='mt-5 max-w-[46ch] text-[17px] leading-relaxed text-muted-foreground'>
                        Agents handle the routine. When something needs judgment, they pause, explain their reasoning and wait for the right person.
                    </p>

                    <ul className='mt-10 space-y-7'>
                        {POINTS.map((point) => (
                            <li key={point.title} className='flex gap-4'>
                                <span className='flex size-11 shrink-0 items-center justify-center rounded-2xl bg-sage text-moss'>
                                    <point.icon aria-hidden className='size-5' />
                                </span>

                                <div>
                                    <h3 className='text-[17px] font-semibold text-ink'>
                                        {point.title}
                                    </h3>

                                    <p className='mt-1 text-[15.5px] leading-relaxed text-muted-foreground'>
                                        {point.body}
                                    </p>
                                </div>
                            </li>
                        ))}
                    </ul>
                </Reveal>

                <Reveal delay={150}>
                    <div className='relative isolate flex min-h-[520px] items-center justify-center overflow-hidden rounded-[36px] bg-[linear-gradient(180deg,#f7dccb_0%,#e9dbee_38%,#b9c6ec_70%,#8ea07c_100%)] p-5 sm:min-h-[600px] sm:rounded-[44px] sm:p-10'>
                        {meadow && (
                            <Image
                                src={meadow.src}
                                alt=''
                                fill
                                sizes='(min-width: 1024px) 600px, 100vw'
                                className='-z-10 object-cover motion-safe:animate-kenburns'
                            />
                        )}

                        <GlassTiles cells={PANEL_TILES} size='clamp(40px, 5vw, 64px)' className='top-6 left-6' />
                        <GlassTiles cells={PANEL_TILES_ALT} size='clamp(34px, 4vw, 52px)' className='right-6 bottom-8' />

                        <div className='relative flex w-full flex-col items-center gap-4'>
                            <div className='flex w-full justify-center motion-safe:animate-float'>
                                <ApprovalDemo />
                            </div>

                            <div className='flex w-full max-w-[390px] items-center gap-3 rounded-[20px] border border-white/70 bg-white/75 px-4 py-3 shadow-[0_20px_50px_-30px_rgba(24,32,78,0.6)] backdrop-blur-xl motion-safe:animate-float sm:-translate-x-10' style={{ animationDelay: '-3.5s' }}>
                                <span aria-hidden className='flex size-9 shrink-0 items-center justify-center rounded-full bg-[#f3d4c0] text-[12px] font-semibold text-[#6b3414]'>
                                    PS
                                </span>

                                <p className='text-[13px] leading-snug text-ink/80'>
                                    <span className='font-semibold text-ink'>Priya Shah</span> was notified in Slack as the finance lead on call.
                                </p>
                            </div>
                        </div>
                    </div>
                </Reveal>
            </div>
        </section>
    )
}
